import { z } from 'zod';
import { widgetTypeSchema } from './widget-dto';

export const dashboardLayouts = [
  'single',
  'two-column',
  'two-row',
  'two-by-two',
  'wide-left',
  'wide-right',
  'wide-top',
  'wide-bottom',
  'three-row',
  'three-column',
  'three-by-two',
  'two-by-three',
  'three-by-three',
  'two-by-two-with-footer',
  'two-by-two-with-header',
] as const;

export const dashboardLayoutSchema = z.enum(dashboardLayouts);

export type DashboardLayout = z.infer<typeof dashboardLayoutSchema>;

export const DASHBOARD_LAYOUT_SLOT_COUNT: Record<DashboardLayout, number> = {
  single: 1,
  'two-column': 2,
  'two-row': 2,
  'two-by-two': 4,
  'wide-left': 3,
  'wide-right': 3,
  'wide-top': 3,
  'wide-bottom': 3,
  'three-row': 3,
  'three-column': 3,
  'three-by-two': 6,
  'two-by-three': 6,
  'three-by-three': 9,
  'two-by-two-with-footer': 5,
  'two-by-two-with-header': 5,
};

// A panel can hold at most one of: a visualization or a widget. Both null = empty slot.
// The DB enforces this with a CHECK constraint (`dashboard_panel_content_xor`).
const panelContentXor = (
  data: { visualizationId: number | null; widgetId: number | null },
  ctx: z.RefinementCtx,
) => {
  if (data.visualizationId !== null && data.widgetId !== null) {
    ctx.addIssue({
      code: 'custom',
      message: 'A panel cannot reference both a visualization and a widget',
      path: ['widgetId'],
    });
  }
};

// Sizing lives on canonical "anchor" panels — no value duplication across panels in
// the same row or column. See dashboard.ts schema comments for the anchor scheme.
//   - widthFraction: row-0 panel of each column carries the column's width. For
//     two-column layouts the row-0 panels' fractions sum to 1; for single-column
//     layouts position 0's fraction can be < 1 to render a narrower grid.
//   - heightPx: column-0 panel of each row carries the row's pixel height.
// Null on either field means "use the layout's default".
export const dashboardPanelDTOSchema = z
  .object({
    id: z.number().int(),
    organizationId: z.string().min(1),
    dashboardId: z.number().int(),
    position: z.number().int().min(0),
    visualizationId: z.number().int().nullable(),
    widgetId: z.number().int().nullable(),
    // Optional on read so older backends (deployed before the sizing migration) keep
    // parsing — their payloads simply omit these fields and the frontend falls back
    // to layout defaults until the backend is updated.
    widthFraction: z.number().min(0).max(1).nullable().optional(),
    heightPx: z.number().int().min(0).nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .superRefine(panelContentXor);

export type DashboardPanelDTO = z.infer<typeof dashboardPanelDTOSchema>;

export const dashboardDTOSchema = z.object({
  id: z.number().int(),
  organizationId: z.string().min(1),
  loopId: z.number().int(),
  name: z.string().nullable(),
  layout: dashboardLayoutSchema,
  panels: z.array(dashboardPanelDTOSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type DashboardDTO = z.infer<typeof dashboardDTOSchema>;

export function parseDashboardDTO(data: unknown): DashboardDTO {
  return dashboardDTOSchema.parse(data);
}

export function getDashboardDisplayName(dashboard: Pick<DashboardDTO, 'id' | 'name'>): string {
  return dashboard.name?.trim() || `Dashboard (${dashboard.id})`;
}

export const DASHBOARD_MAX_NAME_LENGTH = 64;

// Optional initial-content for a panel slot when creating a dashboard.
// Position must be in range for the chosen layout (validated by the service).
//
// At most one of `visualizationId`, `widgetId`, or `widgetType` may be set per panel — all
// null = empty slot. `widgetType` mints a fresh widget row inside the dashboard-create
// transaction so a failed dashboard insert leaves no orphan widgets behind. `widgetId`
// is reserved for slots that should reuse an existing widget (not used by the create UI
// today, but kept for flexibility).
export const dashboardPanelInsertDTOSchema = z
  .object({
    position: z.number().int().min(0),
    visualizationId: z.number().int().nullable(),
    widgetId: z.number().int().nullable(),
    widgetType: widgetTypeSchema.nullable(),
    widthFraction: z.number().min(0).max(1).nullable().optional(),
    heightPx: z.number().int().min(0).nullable().optional(),
  })
  .superRefine((data, ctx) => {
    const setCount =
      (data.visualizationId !== null ? 1 : 0) +
      (data.widgetId !== null ? 1 : 0) +
      (data.widgetType !== null ? 1 : 0);
    if (setCount > 1) {
      ctx.addIssue({
        code: 'custom',
        message:
          'A panel may set at most one of visualizationId, widgetId, or widgetType (or all null for an empty slot)',
        path: ['widgetType'],
      });
    }
  });

export type DashboardPanelInsertDTO = z.infer<typeof dashboardPanelInsertDTOSchema>;

export const dashboardInsertDTOSchema = z
  .object({
    loopId: z.number().int(),
    name: z.string().max(DASHBOARD_MAX_NAME_LENGTH).optional().nullable(),
    layout: dashboardLayoutSchema,
    // If omitted, every panel is seeded empty. If provided, each entry overrides the
    // matching slot (by position). Slots not listed are still seeded empty.
    panels: z.array(dashboardPanelInsertDTOSchema).optional(),
  })
  .strict();

export type DashboardInsertDTO = z.infer<typeof dashboardInsertDTOSchema>;

export const dashboardUpdateDTOSchema = z
  .object({
    name: z.string().max(DASHBOARD_MAX_NAME_LENGTH).optional().nullable(),
    layout: dashboardLayoutSchema.optional(),
  })
  .strict();

export type DashboardUpdateDTO = z.infer<typeof dashboardUpdateDTOSchema>;

// Update a panel. Two independent groups of fields:
//
//   1. Content: `visualizationId` / `widgetId` / `widgetType`. To touch content, pass
//      all three (any null/value combo respecting the XOR rule). Omit them all to
//      leave the existing content untouched. `widgetType` mints a fresh widget row
//      inside the update transaction (same trick used in create).
//
//   2. Sizing: `widthFraction` / `heightPx`. Pass to set, omit to leave untouched.
//      Pass null to clear back to layout defaults.
//
// Resize-only updates omit content; reassign-only updates omit sizing; both can be
// combined in one request if needed.
export const dashboardPanelUpdateDTOSchema = z
  .object({
    visualizationId: z.number().int().nullable().optional(),
    widgetId: z.number().int().nullable().optional(),
    widgetType: widgetTypeSchema.nullable().optional(),
    widthFraction: z.number().min(0).max(1).nullable().optional(),
    heightPx: z.number().int().min(0).nullable().optional(),
  })
  .strict()
  .superRefine((data, ctx) => {
    // Content is all-or-nothing: either all three fields present (any null/value combo,
    // XOR-restricted) or all three absent. A partial payload like `{ widgetType: null }`
    // would unintentionally clear visualizationId/widgetId on the panel.
    const provided = [
      data.visualizationId !== undefined,
      data.widgetId !== undefined,
      data.widgetType !== undefined,
    ];
    const providedCount = provided.filter(Boolean).length;
    if (providedCount > 0 && providedCount < 3) {
      ctx.addIssue({
        code: 'custom',
        message:
          'When updating panel content, all of visualizationId, widgetId, and widgetType must be provided together',
        path: ['widgetType'],
      });
      return;
    }

    // XOR rule on the values themselves when content is being updated.
    const setCount =
      (data.visualizationId != null ? 1 : 0) +
      (data.widgetId != null ? 1 : 0) +
      (data.widgetType != null ? 1 : 0);
    if (setCount > 1) {
      ctx.addIssue({
        code: 'custom',
        message:
          'A panel may set at most one of visualizationId, widgetId, or widgetType (or all null to clear)',
        path: ['widgetType'],
      });
    }
  });

export type DashboardPanelUpdateDTO = z.infer<typeof dashboardPanelUpdateDTOSchema>;

// Atomically swap the content of two panel slots on a dashboard in a single
// transaction so triggers (e.g. orphan-widget cleanup) see the post-swap state.
// `swapWidthFraction` carries the panels' widthFraction along with the content —
// row-orient callers pass true (widths live per-cell-in-row); column-orient
// callers leave it false so column widths stay put when content rearranges.
export const dashboardPanelSwapDTOSchema = z
  .object({
    fromPosition: z.number().int().min(0),
    toPosition: z.number().int().min(0),
    swapWidthFraction: z.boolean().default(false),
  })
  .strict()
  .superRefine((data, ctx) => {
    if (data.fromPosition === data.toPosition) {
      ctx.addIssue({
        code: 'custom',
        message: 'fromPosition and toPosition must differ',
        path: ['toPosition'],
      });
    }
  });

export type DashboardPanelSwapDTO = z.infer<typeof dashboardPanelSwapDTOSchema>;
